import { List, Map, Record } from "immutable";
import { format, parseISO } from "date-fns";

import { defaultValues } from "@config/defaultValues";

import { KEY_PICK, EXTRA_SERVICES_LEGACY } from "@typings/booking";
import {
  ACTION_HIGHLIGHT,
  CANCELLATION_FEE,
  CONTACT_PERIODICITY,
  EXTRA_SERVICES_ENUM,
  IExtraService,
  Job,
  JobAction,
  JOB_ACTION,
  MapOfJobs,
  UI_STATE_ENUM,
  TYPE_ENUM,
  JOB_FETCH_TYPE,
  CANCELLATION_REASON,
  ICancellationInputData,
  ICancellationData,
  Job2DTO,
  CancellationReasonDto,
  CancellationReason,
  RESCHEDULING_TRIGGERED_BY,
  AGENT_REPLACEMENT_STATUS,
} from "@typings/jobs";
import { AVAILABLE_CURRENCIES, PAYMENT_METHODS } from "@typings/globals";

import { DATE_FORMAT } from "@services/DateFormatter";

import {
  AgentReplacement,
  Job2Location,
  Rescheduling,
  Timeslots,
} from "./jobs2.typings";

/*
  JOBS
*/

// job actions
const JobActionFactory = (input: JobAction): Record<JobAction> =>
  Record<JobAction>({
    key: JOB_ACTION.DETAILS,
    highlight: ACTION_HIGHLIGHT.DEFAULT,
    translationKey: "key",
    href: "",
  })(input);

// job extra services
const ExtraServicesFactory = (
  input: IExtraService,
): Record<IExtraService> | any => {
  // @TODO this can be removed when Micheal Owen is gone 💀
  if (input.type === EXTRA_SERVICES_LEGACY.INSIDE_OWEN) {
    return Record<IExtraService>({
      duration: 0,
      type: EXTRA_SERVICES_ENUM.HOME_CLEANING,
    })({ ...input, type: EXTRA_SERVICES_ENUM.INSIDE_OVEN });
  }

  return Record<IExtraService>({
    duration: 0,
    type: EXTRA_SERVICES_ENUM.HOME_CLEANING,
  })(input);
};

// job
export const SingleJobFactory = (input: Job2DTO): Record<Job> =>
  Record<Job>({
    actions: List(),
    agent: "",
    agentPhoneNumber: null,
    agentProfilePictureUrl: null,
    amount: 0,
    amountWithUrssaf: null,
    bathrooms: 0,
    bedrooms: 0,
    canBeRated: false,
    cancellationState: undefined,
    cancellationProcess: undefined,
    cancellationFeeLevel: CANCELLATION_FEE.NO_CANCELLATION_FEE,
    contractPeriodicity: CONTACT_PERIODICITY.ONCE,
    currency: AVAILABLE_CURRENCIES.CHF,
    duration: 0,
    executionDate: "1970-01-01",
    extraServices: List(),
    floorAndDoor: "",
    hasCancellationFee: false,
    rescheduledAutomatically: false,
    keyPick: KEY_PICK.SOMEONE_AT_HOME,
    location: "",
    locationComment: "",
    locationUuid: "",
    rating: 0,
    ratingComment: "",
    specialInstructions: null,
    uiState: UI_STATE_ENUM.UPCOMING,
    type: TYPE_ENUM.HOME_CLEANING,
    uuid: "0",
    agentUuid: "0",
    warrantyIncluded: false,
    subscriptionUuid: null,
    contractUuid: "",
    hasOnboardingCompleted: false,
    cleaningChecklist: Record({
      canBeFilled: false,
      isCreated: false,
    })(),
    rescheduling: Record({
      rescheduled: false,
      triggeredBy: RESCHEDULING_TRIGGERED_BY.NONE,
      pendingClientPreferredDateRequest: false,
    })(),
    agentAbsenceAutomationProcess: Record({
      status: AGENT_REPLACEMENT_STATUS.NONE,
      rescheduleDetails: {
        triggeredBy: RESCHEDULING_TRIGGERED_BY.NONE,
      },
      optInDetails: {
        absentAgentFullName: null,
      },
    })(),
  })({
    ...input,
    cancellationProcess: undefined,
    executionDate: format(
      parseISO(input.executionDate),
      DATE_FORMAT.YEAR_MONTH_DAY_HOUR_MINUTE_ISO_8601,
    ),
    actions: List(input.actions.map(JobActionFactory)),
    extraServices: List(input.extraServices.map(ExtraServicesFactory)),
    cleaningChecklist: Record({
      canBeFilled: input.cleaningChecklist.canBeFilled,
      isCreated: input.cleaningChecklist.isCreated,
    })(),
    rescheduling: Record({
      rescheduled: false,
      triggeredBy: RESCHEDULING_TRIGGERED_BY.NONE,
      pendingClientPreferredDateRequest: false,
    })(input.rescheduling),
    agentAbsenceAutomationProcess: Record({
      status: AGENT_REPLACEMENT_STATUS.NONE,
      rescheduleDetails: {
        triggeredBy: RESCHEDULING_TRIGGERED_BY.NONE,
      },
      optInDetails: {
        absentAgentFullName:
          input.agentAbsenceAutomationProcess.optInDetails.absentAgentFullName,
      },
    })(input.agentAbsenceAutomationProcess),
  });

// upcoming/previous map of jobs (records)
export const MapJobsFactory = (
  input: Job2DTO[],
  initialData: MapOfJobs = Map(),
  areJobsUpcoming?: boolean,
): MapOfJobs => {
  return input
    .reduce((acc: Map<string, Record<Job>>, item: Job2DTO) => {
      return acc.set(item.uuid, SingleJobFactory(item));
    }, initialData)
    .sort((prevItem: Record<Job>, item: Record<Job>) => {
      if (areJobsUpcoming) {
        return (
          +new Date(prevItem.get("executionDate")) -
          +new Date(item.get("executionDate"))
        );
      }

      return (
        +new Date(item.get("executionDate")) -
        +new Date(prevItem.get("executionDate"))
      );
    });
};

// location
export const JobsLocationFactory = (
  input: Job2Location,
): Record<Job2Location> =>
  Record<Job2Location>({
    hasNextPagePrevious: false,
    hasNextPageUpcoming: false,
    cursor: "",
    [JOB_FETCH_TYPE.PREVIOUS]: Map(),
    [JOB_FETCH_TYPE.UPCOMING]: Map(),
  })(input);

/*
  CANCELLATION
*/

const ReasonsFactory = (
  input: CANCELLATION_REASON[],
): List<CANCELLATION_REASON> =>
  List(
    input.map((item: CANCELLATION_REASON | { reason: CANCELLATION_REASON }) =>
      typeof item === "string" ? item : item.reason,
    ),
  );

export const CancellationFactory = (
  input: ICancellationInputData,
): Record<ICancellationData> =>
  Record<ICancellationData>({
    cancellationRequestUuid: "",
    currency: "CHF",
    feeInHours: 0,
    feeAmount: 0,
    reasons: List(),
    showIsCancellingContract: false,
    showIsFeeSkippedByAdmin: false,
    isFreeCancellation: true,
    freeCancellationsLeft: null,
    lateJobCancellationThreshold: null,
    feeLevel: null,
    showIsCancellationCountingSkippedByAdmin: false,
    changeRequestRelativeUrl: "",
    customReasons: {
      description: "",
      heading: "",
      reasons: [],
    },
  })({
    ...input,
    customReasons: customReasonsFactory(
      input.customReasons as CancellationReasonDto,
    ),
    reasons: List.isList(input.reasons)
      ? input.reasons
      : ReasonsFactory(input.reasons),
  });

const customReasonsFactory = (
  reasons: CancellationReasonDto,
): CancellationReason | null => {
  if (!reasons) {
    return null;
  }

  return {
    heading: reasons.heading || "",
    description: reasons.description || "",
    reasons: Array.isArray(reasons.reasons)
      ? reasons.reasons.map(reason => ({
          finishType: Array.isArray(reason.finishType)
            ? reason.finishType
            : ["CANCEL", "EDIT"],
          name: String(reason.name),
          subReasons: reason.subReasons
            ? customReasonsFactory(reason.subReasons)
            : null,
          commentField: !!reason.commentField,
          uuid: String(reason.uuid),
        }))
      : [],
  };
};

export const CancellationPaymentFactory = (
  input: { method: PAYMENT_METHODS }[],
): List<PAYMENT_METHODS> =>
  List(input.map((item: { method: PAYMENT_METHODS }) => item.method));

export const reschedulingFactory = (
  input: Rescheduling,
): Record<Rescheduling> =>
  Record<Rescheduling>({
    reason: "",
    previousCleaningDate: "",
    currentCleaningDate: "",
    changeRequestRelativeUrl: "",
    previousCleaningDuration: 0,
    currentCleaningDuration: 0,
    appliedStartingFrom: "",
    triggeredBy: RESCHEDULING_TRIGGERED_BY.NONE,
    timeslots: Record(defaultValues.rescheduling.timeslots)(),
  })(input);

export const reschedulingTimeslotsFactory = (
  input: Timeslots,
): Record<Timeslots> =>
  Record<Timeslots>(defaultValues.rescheduling.timeslots)(input);

export const agentReplacementFactory = (
  input: AgentReplacement,
): Record<AgentReplacement> =>
  Record<AgentReplacement>({
    previousCleaningDate: "",
    previousCleaningDuration: 0,
    currentCleaningDate: "",
    currentCleaningDuration: 0,
    changeRequestRelativeUrl: "",
    previousAgent: { id: "", fullName: "" },
    newAgent: { id: "", fullName: "" },
  })(input);
